import { dictionaryApi } from './dictionaryClient';
import { normalizeGana, type Gana } from './ganaApi';

export type PadachhedGanaLink = {
  id: number;
  ashtadhyayiPadachhedId: number;
  ganaId: number;
  gana: Gana | null;
};

export type CreatePadachhedGanaLinkBody = {
  ashtadhyayiPadachhedId: number;
  ganaId: number;
};

export type UpdatePadachhedGanaLinkBody = {
  ganaId?: number;
};

function isRecord(x: unknown): x is Record<string, unknown> {
  return x != null && typeof x === 'object' && !Array.isArray(x);
}

function unwrapEntityPayload(raw: unknown): unknown {
  if (isRecord(raw) && 'data' in raw) return raw.data;
  return raw;
}

function num(o: Record<string, unknown>, key: string): number | null {
  const v = o[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

export function normalizePadachhedGanaLink(raw: unknown): PadachhedGanaLink | null {
  if (!isRecord(raw)) return null;
  const id = num(raw, 'id');
  const ashtadhyayiPadachhedId =
    num(raw, 'ashtadhyayiPadachhedId') ?? num(raw, 'ashtadhyayi_padachhed_id');
  const ganaId = num(raw, 'ganaId') ?? num(raw, 'gana_id');
  if (id == null || ashtadhyayiPadachhedId == null || ganaId == null) return null;

  const ganaRaw = raw.gana;
  const gana = ganaRaw ? normalizeGana(ganaRaw) : null;

  return {
    id,
    ashtadhyayiPadachhedId,
    ganaId,
    gana,
  };
}

function normalizeList(data: unknown): PadachhedGanaLink[] {
  const rows =
    Array.isArray(data) ? data : isRecord(data) && Array.isArray(data.data) ? (data.data as unknown[]) : [];
  return rows.map(normalizePadachhedGanaLink).filter((x): x is PadachhedGanaLink => x != null);
}

export async function listPadachhedGanaLinks(filters?: {
  ashtadhyayiPadachhedId?: number;
  ganaId?: number;
}): Promise<PadachhedGanaLink[]> {
  const params: Record<string, number> = {};
  if (filters?.ashtadhyayiPadachhedId != null) {
    params.ashtadhyayiPadachhedId = filters.ashtadhyayiPadachhedId;
  }
  if (filters?.ganaId != null) {
    params.ganaId = filters.ganaId;
  }
  const res = await dictionaryApi.get<unknown>('/ashtadhyayi-padachhed-gana', {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
  return normalizeList(res.data);
}

export async function getPadachhedGanaLink(id: number): Promise<PadachhedGanaLink> {
  const res = await dictionaryApi.get<unknown>(`/ashtadhyayi-padachhed-gana/${id}`);
  const row = normalizePadachhedGanaLink(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid padachhed–gana link response');
  return row;
}

export async function createPadachhedGanaLink(
  body: CreatePadachhedGanaLinkBody
): Promise<PadachhedGanaLink> {
  const res = await dictionaryApi.post<unknown>('/ashtadhyayi-padachhed-gana', body);
  const row = normalizePadachhedGanaLink(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid padachhed–gana link response');
  return row;
}

export async function updatePadachhedGanaLink(
  id: number,
  body: UpdatePadachhedGanaLinkBody
): Promise<PadachhedGanaLink> {
  const res = await dictionaryApi.patch<unknown>(`/ashtadhyayi-padachhed-gana/${id}`, body);
  const row = normalizePadachhedGanaLink(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid padachhed–gana link response');
  return row;
}

export async function deletePadachhedGanaLink(id: number): Promise<void> {
  await dictionaryApi.delete(`/ashtadhyayi-padachhed-gana/${id}`);
}
